import React, {useState, useEffect} from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isRegister, userToken, logout } from "../../features/user";
import axios from "axios";
import { API_OK, API_URL, DEFAULT_ERROR_TEXT, API_EXCEPTION } from '../../Constants';
import {Trans,  useTranslation } from 'react-i18next';
import {checkApiSessionErrorMessage, delay, getLang, supportDetails} from "../../Helper"
import { useRetryCountdown } from '../../hooks/useRetryCountdown';
import RetryNotice from '../widgets/RetryNotice';

import Frame from '../widgets/Frame';
import { useSubmitGuard } from "../../hooks/useSubmitGuard";
import { useCaptcha } from "../../captcha";

export function VerificationPhoneCodeType() {
    const [state, setState] = useState({ errors: {}, message: null  });

    const reseller = useSelector((state) => {
        return state.reseller.value
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Both send buttons share one guard: while a code is going out, neither channel
    // may start a second send - and each send counts against the same budget.
    const submit = useSubmitGuard();
    /**
     * הקאפצ'ה. פעילה רק כשההגדרה הגיעה מהשרת, ומה שנשלח נבדק שם בלי קשר למסך.
     *
     * טוקן אחד למסך: השליחה מחדש והבדיקה חולקות פעולה, כי הן חולקות טופס.
     */
    const captcha = useCaptcha('verifyCode');
    const isUserRegister = useSelector(isRegister);
    const retry = useRetryCountdown();

    /**
     * "still stuck? call us" - with the reseller's own phone and email.
     *
     * The sentence used to name Yemot's support line and mailbox in the translation key
     * itself, so every reseller's customer was sent to another supplier's help desk. Worse
     * in English: the key was missing from `en/translations.json`, so i18next fell back to
     * the key, and the hardcoded numbers were the text.
     *
     * One of the three keys, by what the reseller actually gave - a reseller who gave
     * neither gets no sentence at all, because "contact support" with no way to do so is
     * not help.
     */
    const supportLine = (() => {
        const { phone, email } = supportDetails(reseller);
        if (phone && email) {
            return t("If you are still experiencing issues, please contact our support via phone number {{phone}} or via the email {{email}}.", { phone, email });
        }
        if (phone) {
            return t("If you are still experiencing issues, please contact our support via phone number {{phone}}.", { phone });
        }
        if (email) {
            return t("If you are still experiencing issues, please contact our support via the email {{email}}.", { email });
        }
        return null;
    })();

    useEffect(()=>{
        if(!isUserRegister){
           navigate("/register");
        }
        // eslint-disable-next-line
    },[]);

    const handleResendCode = async (e, communicationPref) => {
        e.preventDefault();
        if(retry.isWaiting){
            return;
        }
        let errorMessage = DEFAULT_ERROR_TEXT;

        let config = {'headers': {"token": userToken(), "lang": getLang()}};

        const data = {communicationPref: communicationPref};

        // הטוקן מצורף כאן, כדי שהפעולה שהוא נוצר עבורה תיקבע במקום אחד.
        const payload = await captcha.attach(data);
        // אין טוקן - אין מה לשלוח. ההסבר כבר מצויר על המסך.
        if (!payload) {
            return;
        }

        try {
            const reply = await submit.run(() => axios.post(API_URL + 'sendPhoneVerificationCode', payload, config));
            if (!reply) {
                return;
            }
            if(reply.hasOwnProperty('data') && reply.data.hasOwnProperty('responseStatus') && reply.data.responseStatus === API_OK){
                //show message sent
                setState({
                    ...state,
                    errors: {},
                    message: "A new verification code is on its way"
                });
                await delay(1000);
                navigate('/verify-phone');

            }else{
                if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
                    errorMessage = reply.data["message"];
                }
                if(!checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
                    let errors = {server_error: errorMessage};

                    // Server reports the remaining lockout as fractional minutes;
                    // turn it into a live countdown that gates the resend buttons.
                    if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("tryAgainInMinutes"))){
                        retry.startFromMinutes(reply.data["tryAgainInMinutes"]);
                    }

                    setState({
                        ...state,
                        errors: errors,
                        message: null
                    });
                }

            };
        } catch (e) {
            setState({
                ...state,
                errors: {server_error: errorMessage},
                message: null
            });
        }
    };

    const editPhone = (e) => {
        e.preventDefault();
        navigate("/register");
    };


    return (
            <Frame>
                <div className='main main-verification-code-type'>
                    <div className='main-top'>
                        <h1>{t("Problem receiving the verification code")}</h1>
                    </div>
                    <div className='main-body'>
                        <div className='right-main'>
                            <div className='right-main-inner'>
                                <h2>{t("We are sorry for the inconvenience. Here are a few options that can help")}</h2>
                                {captcha.node}
                                <div {...submit.buttonProps({base: 'button button-space-between button-teal', blocked: retry.isWaiting})} onClick={(e) => handleResendCode(e, 'SMS')}>  <div className='mr20'>{submit.busy ? t("Sending...") : t("Please send me a new code via SMS")}</div> {submit.busy ? <span className='button-spinner ml20' /> : <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>} </div>
                                <div {...submit.buttonProps({base: 'button button-space-between button-teal', blocked: retry.isWaiting})} onClick={(e) => handleResendCode(e, 'VOICE')}>  <div className='mr20'>{submit.busy ? t("Sending...") : t("Please send me a new code via phone call")}</div> {submit.busy ? <span className='button-spinner ml20' /> : <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>} </div>
                                <div className='button button-space-between button-teal' onClick={(e) => editPhone(e)}>  <div className='mr20'>{t("Update my phone number")}</div> <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/> </div>
                                {state.message?<h2 className='teal mt10'><Trans i18nKey={state.message} /></h2>:""}
                                {Object.entries(state.errors).map(([key, msg]) => (
                                    <h2 key={key} className='mt20 error'><Trans i18nKey={msg} /></h2>
                                ))}
                                <RetryNotice retry={retry} />
                                {supportLine ? <h2 className='mt50 teal'>{supportLine}</h2> : ""}
                                {/* <h2 className='mt50 teal'><Trans i18nKey="l6" values={{ email: 'email', phoneNumber: 'phoneNumber' }}/></h2> */}
                                {/* {state.error?<h2 className='mt40 error'><Trans i18nKey="f1" /></h2>:""}
                                {state.error?<h2><Trans i18nKey="f2" /></h2>:""} */}
                            </div>
                        </div>
                        <div className='left-main'>
                            <img src='/images/person-magnifying-glass.png' className='content-logo' alt=''/>
                        </div>
                    </div>
                </div>
            </Frame>
    );
}